/**
 * Unpaid Report Settings — daily digest email of appointments with an
 * outstanding balance in the last N days. Toggle on/off, choose recipient,
 * send time (Australia/Melbourne), and rolling lookback window. "Send test"
 * fires the report to any email for the current window.
 */
import { isoDate as melDate } from "@/lib/intl";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, Save, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return melDate(d);
}

export default function UnpaidReportSettings() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sendTime, setSendTime] = useState("07:00");
  const [lookback, setLookback] = useState(7);
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);

  async function refresh() {
    try {
      const r = await api.get("/unpaid-report/config");
      setCfg(r.data);
      setEnabled(!!r.data.enabled);
      setRecipient(r.data.recipient_email || "");
      setSendTime(r.data.send_time || "07:00");
      setLookback(r.data.lookback_days || 7);
    } catch (e) {
      toast.error("Failed to load unpaid report config", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put("/unpaid-report/config", {
        enabled,
        recipient_email: recipient,
        send_time: sendTime,
        lookback_days: Number(lookback),
      });
      setCfg(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    const to = (testEmail || recipient || "").trim();
    if (!to) { toast.error("Enter an email to send the test to"); return; }
    setSending(true);
    try {
      const r = await api.post("/unpaid-report/send-now", {
        to_email: to,
        min_date: isoDaysAgo(lookback),
        max_date: isoDaysAgo(1),
      });
      if (r.data.sent) {
        toast.success(`Sent ${r.data.count} unpaid appt${r.data.count === 1 ? "" : "s"} · $${(r.data.total_amount_owed || 0).toFixed(2)}`, {
          description: `Delivered to ${to}`,
        });
      } else {
        toast.error("Send failed", { description: r.data.error || "unknown error" });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  async function runNow() {
    setRunning(true);
    try {
      const r = await api.post("/unpaid-report/run-daily?dry_run=false");
      if (r.data.sent) {
        toast.success(`Daily digest sent · ${r.data.count} unpaid`, {
          description: `${r.data.recipient} · $${(r.data.total_amount_owed || 0).toFixed(2)} owed`,
        });
      } else if (r.data.skipped) {
        toast.message("Skipped", { description: r.data.skipped });
      } else {
        toast.error("Daily run failed", { description: r.data.error || "unknown error" });
      }
    } catch (e) {
      toast.error("Daily run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="unpaid-report-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <AlertCircle className="h-3.5 w-3.5" /> Unpaid appointments · daily digest
          </div>
          <h3 className="mt-1 font-display text-xl tracking-tight text-zinc-950">
            Get emailed every morning about unpaid classes
          </h3>
          <p className="mt-1 text-xs text-zinc-600 max-w-xl">
            Sends a daily HTML digest of the last N days of active appointments
            with an outstanding balance. Grouped by Checked In / DNA / LCXL /
            Unlabelled. Free appointment types (price = 0) are excluded.
            Uses the shared Resend integration configured in Daily Schedule.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label className="text-xs text-zinc-600" htmlFor="unpaid-enabled-switch">Enabled</Label>
          <Switch
            id="unpaid-enabled-switch"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="unpaid-enabled-switch"
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Recipient email</Label>
          <Input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="manager@yourbusiness.com"
            className="border-zinc-300 mt-1.5"
            data-testid="unpaid-recipient-input"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Send time (AU)</Label>
          <Input
            type="time"
            value={sendTime}
            onChange={(e) => setSendTime(e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="unpaid-send-time"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Rolling lookback (days)
          </Label>
          <Input
            type="number"
            min="1"
            max="90"
            value={lookback}
            onChange={(e) => setLookback(e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            data-testid="unpaid-lookback"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-zinc-100 pt-5">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="unpaid-save-btn"
        >
          <Save className={`h-4 w-4 mr-1.5 ${saving ? "animate-pulse" : ""}`} />
          {saving ? "Saving…" : "Save"}
        </Button>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">
            Send test to
          </Label>
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={recipient || "you@example.com"}
            className="border-zinc-300 mt-1.5"
            data-testid="unpaid-test-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={sendTest}
          disabled={sending}
          className="border-zinc-300 hover:bg-zinc-100"
          data-testid="unpaid-send-test-btn"
        >
          <Send className={`h-4 w-4 mr-1.5 ${sending ? "animate-pulse" : ""}`} />
          {sending ? "Sending…" : "Send test"}
        </Button>
        <Button
          variant="outline"
          onClick={runNow}
          disabled={running || !enabled || !recipient}
          className="border-zinc-300 hover:bg-zinc-100"
          data-testid="unpaid-run-daily-btn"
          title="Run today's scheduled daily job right now (respects atomic dedup)"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${running ? "animate-spin" : ""}`} />
          {running ? "Running…" : "Run daily now"}
        </Button>
      </div>

      {cfg?.updated_at && (
        <p className="mt-3 text-2xs text-zinc-400 font-mono-num">
          Last saved {cfg.updated_at.slice(0, 16).replace("T", " ")}
        </p>
      )}
    </Card>
  );
}
